import React, { useState, useEffect, useMemo, useRef } from "react";
import { Search, Filter, UserPlus, Plus, Edit, Trash2, X, Eye, EyeOff, ChevronLeft, ChevronRight } from "lucide-react";
import DataTable from "react-data-table-component";
import Modal from "react-modal";
import { adminServices } from "../../services/AdminServices";
import LoadingModal from "../../components/LoadingModal";
import { toast } from "react-toastify";
const paginationStyles = {
  wrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    padding: "16px 0",
    gap: "8px",
    flexWrap: "wrap"
  },
  link: {
    color: "#6b7280",
    fontSize: "0.875rem",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: "none",
    border: "none",
    padding: "6px 8px"
  },
  linkDisabled: {
    color: "#9ca3af",
    cursor: "not-allowed",
    opacity: 0.7
  },
  pageNum: {
    color: "#6b7280",
    fontSize: "0.875rem",
    cursor: "pointer",
    minWidth: "32px",
    height: "32px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 8px",
    border: "none",
    backgroundColor: "transparent",
    borderRadius: "6px"
  },
  pageNumActive: {
    backgroundColor: "#e5e7eb",
    color: "#374151",
    fontWeight: 500
  },
  ellipsis: {
    color: "#6b7280",
    fontSize: "0.875rem",
    padding: "0 4px",
    minWidth: "24px",
    textAlign: "center"
  }
};
function CustomPagination({
  rowCount,
  rowsPerPage,
  currentPage,
  onChangePage,
  totalRows
}) {
  const total = rowCount ?? totalRows ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;
  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({
        length: totalPages
      }, (_, i) => i + 1);
    }
    const pages = [];
    if (currentPage <= 4) {
      for (let i = 1; i <= Math.min(5, totalPages); i++) pages.push(i);
      if (totalPages > 5) pages.push("...");
      if (totalPages > 5) pages.push(totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1);
      if (totalPages > 5) pages.push("...");
      for (let i = Math.max(1, totalPages - 4); i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("...");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };
  const pageNumbers = getPageNumbers();
  return <div style={paginationStyles.wrapper}>
      <button type="button" aria-label="Previous page" style={{
      ...paginationStyles.link,
      ...(!canPrev ? paginationStyles.linkDisabled : {})
    }} onClick={() => canPrev && onChangePage(currentPage - 1)} disabled={!canPrev}>
        <ChevronLeft size={18} />
        Previous
      </button>
      {pageNumbers.map((p, i) => p === "..." ? <span key={`ellipsis-${i}`} style={paginationStyles.ellipsis}>
            ...
          </span> : <button key={p} type="button" style={{
      ...paginationStyles.pageNum,
      ...(p === currentPage ? paginationStyles.pageNumActive : {})
    }} onClick={() => onChangePage(p)}>
            {p}
          </button>)}
      <button type="button" aria-label="Next page" style={{
      ...paginationStyles.link,
      ...(!canNext ? paginationStyles.linkDisabled : {})
    }} onClick={() => canNext && onChangePage(currentPage + 1)} disabled={!canNext}>
        Next
        <ChevronRight size={18} />
      </button>
    </div>;
}
const UserAccess = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRoleFilter, setSelectedRoleFilter] = useState("All Roles");
  const [showRoleFilterDropdown, setShowRoleFilterDropdown] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isAddRolesModalOpen, setIsAddRolesModalOpen] = useState(false);
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);
  const [isDeactivateUserModalOpen, setIsDeactivateUserModalOpen] = useState(false);
  const [isGeneratePasswordModalOpen, setIsGeneratePasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [colonies, setColonies] = useState([]);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [selectedEditFacilities, setSelectedEditFacilities] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [editingUserId, setEditingUserId] = useState(null);

  // Fetch roles from API
  useEffect(() => {
    fetchRoles();
    fetchColonies();
    fetchUsers();
    fetchPermissions();
  }, []);
  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await adminServices.getData('Roles');
      if (response && response.data) {
        // Map API response to roles list
        const rolesList = Array.isArray(response.data) ? response.data.filter(role => role.status === 1 || role.status === "Active") // Filter active roles
        .map(role => ({
          id: role.roleId,
          name: role.roleName,
          code: role.roleCode,
          status: role.status
        })) : [];
        setRoles(rolesList);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };
  const fetchColonies = async () => {
    try {
      const response = await adminServices.storeProcedure2('getColonies', {});
      if (response && response.data) {
        const coloniesList = Array.isArray(response.data) ? response.data.map(colony => ({
          colonyId: colony.colony_id,
          colonyName: colony.colony_name,
          facilities: JSON.parse(colony.facilities || '[]')
        })) : [];
        setColonies(coloniesList);
      }
    } catch (error) {
      console.error("Error fetching colonies:", error);
      setColonies([]);
    }
  };
  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Try to fetch all users including inactive ones
      // If the API supports includeInactive parameter, use it; otherwise try without filter
      const response = await adminServices.getData('UserManagement?includeInactive=true');
      if (!response || !response.data) {
        // Fallback: try without parameter
        const fallbackResponse = await adminServices.getData('UserManagement');
        if (fallbackResponse && fallbackResponse.data) {
          const mappedUsers = Array.isArray(fallbackResponse.data) ? fallbackResponse.data.map((user, index) => {
            // Format date
            const formatDate = dateString => {
              if (!dateString) return "";
              const date = new Date(dateString);
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              const year = String(date.getFullYear()).slice(-2);
              const minutes = String(date.getMinutes()).padStart(2, '0');
              const ampm = date.getHours() >= 12 ? 'PM' : 'AM';
              const displayHours = date.getHours() % 12 || 12;
              return `${month}/${day}/${year} ${displayHours}:${minutes}${ampm}`;
            };

            // Get role names (join multiple roles)
            const roleNames = user.roles && user.roles.length > 0 ? user.roles.map(role => role.roleName).join(", ") : "No Role";

            // Determine status - handle both numeric (0/1) and string ("Active"/"Inactive") formats
            // Default to "Inactive" if status is undefined/null
            let userStatus = "Inactive";
            if (user.status === 1 || user.status === "Active" || user.status === "active") {
              userStatus = "Active";
            } else if (user.status === 0 || user.status === "Inactive" || user.status === "inactive") {
              userStatus = "Inactive";
            }
            return {
              id: user.userId || index + 1,
              name: `${user.firstName || ""} ${user.lastName || ""}`.trim() || "-",
              email: user.email || "-",
              role: roleNames,
              createTime: formatDate(user.regDate) || "-",
              lastLoginTime: formatDate(user.lastLoginTime) || "-",
              status: userStatus,
              originalData: user // Keep original data for edit
            };
          }) : [];

          //console.log("Fetched users (fallback):", mappedUsers.length, "Active:", mappedUsers.filter(u => u.status === "Active").length, "Inactive:", mappedUsers.filter(u => u.status === "Inactive").length);
          setUsersData(mappedUsers);
          toast.success("Users loaded successfully");
          setLoading(false);
          return;
        }
      }
      if (response && response.data) {
        // Map API response to table format - include ALL users (active and inactive)
        const mappedUsers = Array.isArray(response.data) ? response.data.map((user, index) => {
          // Format date
          const formatDate = dateString => {
            if (!dateString) return "";
            const date = new Date(dateString);
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const year = String(date.getFullYear()).slice(-2);
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const ampm = date.getHours() >= 12 ? 'PM' : 'AM';
            const displayHours = date.getHours() % 12 || 12;
            return `${month}/${day}/${year} ${displayHours}:${minutes}${ampm}`;
          };

          // Get role names (join multiple roles)
          const roleNames = user.roles && user.roles.length > 0 ? user.roles.map(role => role.roleName).join(", ") : "No Role";

          // Determine status - handle both numeric (0/1) and string ("Active"/"Inactive") formats
          // Default to "Inactive" if status is undefined/null
          let userStatus = "Inactive";
          if (user.status === 1 || user.status === "Active" || user.status === "active") {
            userStatus = "Active";
          } else if (user.status === 0 || user.status === "Inactive" || user.status === "inactive") {
            userStatus = "Inactive";
          }
          return {
            id: user.userId || index + 1,
            name: `${user.firstName || ""} ${user.lastName || ""}`.trim() || "-",
            email: user.email || "-",
            role: roleNames,
            createTime: formatDate(user.regDate) || "-",
            lastLoginTime: formatDate(user.lastLoginTime) || "-",
            status: userStatus,
            originalData: user // Keep original data for edit
          };
        }) : [];

        // Log to verify we're getting all users including inactive ones
        //console.log("Fetched users:", mappedUsers.length, "Active:", mappedUsers.filter(u => u.status === "Active").length, "Inactive:", mappedUsers.filter(u => u.status === "Inactive").length);
        setUsersData(mappedUsers);
        toast.success("Users loaded successfully");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsersData([]);
    } finally {
      setLoading(false);
    }
  };
  const fetchPermissions = async () => {
    try {
      const response = await adminServices.getData('Permissions');
      if (response && response.data) {
        const permissionsList = Array.isArray(response.data) ? response.data.map(permission => ({
          id: permission.id,
          module: permission.module || "",
          key: permission.key || "",
          description: permission.description || ""
        })) : [];
        setPermissions(permissionsList);
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setPermissions([]);
    }
  };
  const handleFacilityToggle = (colonyId, facilityName) => {
    const facilityKey = `${colonyId}-${facilityName}`;
    setSelectedFacilities(prev => {
      if (prev.includes(facilityKey)) {
        return prev.filter(key => key !== facilityKey);
      } else {
        return [...prev, facilityKey];
      }
    });
  };
  const handleStatusUpdate = async (userId, newStatus) => {
    // newStatus: 1 for Active, 0 for Inactive
    setLoading(true);
    try {
      const response = await adminServices.patchData(`UserManagement/${userId}/status?status=${newStatus}`);
      if (response.status === 200) {
        // Refresh user list
        await fetchUsers();
        alert(`User status updated to ${newStatus === 1 ? 'Active' : 'Inactive'} successfully!`);
      }
    } catch (error) {
      console.error("Error updating user status:", error);
      alert(error.response?.data?.message || "Failed to update user status. Please try again.");
      // Refresh to revert UI state
      await fetchUsers();
    } finally {
      setLoading(false);
    }
  };
  const fetchUserDetails = async userId => {
    setLoading(true);
    try {
      const response = await adminServices.getData(`UserManagement/${userId}`);
      if (response && response.data) {
        const user = response.data;

        // Map facilities to selectedFacilities format
        const facilitiesKeys = user.facilities?.map(facility => `${facility.colonyId}-${facility.facilityName}`) || [];

        // Map roles to role IDs
        const roleIds = user.roles?.map(role => role.roleId) || [];
        setEditFormData({
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          mobileNumber: user.mobileNumber || "",
          password: "",
          // Don't show password, user needs to enter new one if changing
          facilities: [],
          roles: roleIds
        });
        setSelectedEditFacilities(facilitiesKeys);
        setEditingUserId(userId);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      alert("Failed to load user details. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const handleUpdateUser = async () => {
    if (!editingUserId) return;

    // Validation
    const missingFields = [];
    if (!editFormData.firstName || !editFormData.firstName.trim()) {
      missingFields.push("First Name");
    }
    if (!editFormData.lastName || !editFormData.lastName.trim()) {
      missingFields.push("Last Name");
    }
    if (!editFormData.email || !editFormData.email.trim()) {
      missingFields.push("Email");
    }
    if (missingFields.length > 0) {
      alert(`Please fill in the following required fields: ${missingFields.join(", ")}`);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editFormData.email.trim())) {
      alert("Please enter a valid email address");
      return;
    }
    if (editFormData.roles.length === 0) {
      alert("Please select at least one role");
      return;
    }

    // Build facilities array from selected facilities
    const facilitiesArray = selectedEditFacilities.map(key => {
      const [colonyId, facilityName] = key.split('-');
      return {
        colonyId: parseInt(colonyId),
        facilityName: facilityName
      };
    });

    // Build request payload
    const payload = {
      firstName: editFormData.firstName.trim(),
      lastName: editFormData.lastName.trim(),
      email: editFormData.email.trim(),
      mobileNumber: editFormData.mobileNumber.trim() || "",
      facilities: facilitiesArray,
      roles: editFormData.roles
    };

    // Only include password if it's been changed (not empty)
    if (editFormData.password && editFormData.password.trim()) {
      payload.password = editFormData.password.trim();
    }
    setLoading(true);
    try {
      const response = await adminServices.updateData(`UserManagement/${editingUserId}`, payload);
      if (response.status === 200 || response.status === 204) {
        toast.success("User updated successfully!");
        // Reset form
        setEditFormData({
          firstName: "",
          lastName: "",
          email: "",
          mobileNumber: "",
          password: "",
          facilities: [],
          roles: []
        });
        setSelectedEditFacilities([]);
        setEditingUserId(null);
        setIsEditUserModalOpen(false);
        // Refresh user list
        fetchUsers();
      }
    } catch (error) {
      console.error("Error updating user:", error);
      alert(error.response?.data?.message || "Failed to update user. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const handleDeleteUser = async () => {
    const userId = selectedUser?.originalData?.userId || selectedUser?.id;
    if (!userId) {
      alert("Error: User ID not found");
      return;
    }
    setLoading(true);
    try {
      const response = await adminServices.deleteData(`UserManagement/${userId}`);
      if (response.status === 200 || response.status === 204) {
        alert("User deleted successfully!");
        setIsDeleteUserModalOpen(false);
        setSelectedUser(null);
        // Refresh user list
        await fetchUsers();
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      alert(error.response?.data?.message || "Failed to delete user. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const handleAddRole = async () => {
    // Validation
    if (!roleFormData.roleTitle || !roleFormData.roleTitle.trim()) {
      alert("Please enter a Role Title");
      return;
    }

    // Generate roleCode from roleTitle if not provided
    const roleCode = roleFormData.roleCode.trim() || roleFormData.roleTitle.trim().toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');

    // Build request payload
    const payload = {
      roleId: 0,
      roleName: roleFormData.roleTitle.trim(),
      roleCode: roleCode,
      status: roleFormData.status || 1,
      permissionIds: roleFormData.permissionIds || []
    };
    setLoading(true);
    try {
      const response = await adminServices.postData('Roles', payload);
      if (response.status === 200 || response.status === 201) {
        toast.success("Role created successfully!");
        // Reset form
        setRoleFormData({
          roleTitle: "",
          roleCode: "",
          description: "",
          status: 1,
          permissionIds: []
        });
        setIsAddRolesModalOpen(false);
        // Refresh roles list
        await fetchRoles();
      }
    } catch (error) {
      console.error("Error creating role:", error);
      alert(error.response?.data?.message || "Failed to create role. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const handleAddUser = async () => {
    // Validation with specific error messages
    const missingFields = [];
    if (!addFormData.firstName || !addFormData.firstName.trim()) {
      missingFields.push("First Name");
    }
    if (!addFormData.lastName || !addFormData.lastName.trim()) {
      missingFields.push("Last Name");
    }
    if (!addFormData.email || !addFormData.email.trim()) {
      missingFields.push("Email");
    }
    if (!addFormData.password || !addFormData.password.trim()) {
      missingFields.push("Password");
    }
    if (missingFields.length > 0) {
      alert(`Please fill in the following required fields: ${missingFields.join(", ")}`);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(addFormData.email.trim())) {
      alert("Please enter a valid email address");
      return;
    }
    if (addFormData.roles.length === 0) {
      alert("Please select at least one role");
      return;
    }

    // Build facilities array from selected facilities
    const facilitiesArray = selectedFacilities.map(key => {
      const [colonyId, facilityName] = key.split('-');
      return {
        colonyId: parseInt(colonyId),
        facilityName: facilityName
      };
    });

    // Build request payload
    const payload = {
      firstName: addFormData.firstName.trim(),
      lastName: addFormData.lastName.trim(),
      email: addFormData.email.trim(),
      password: addFormData.password,
      mobileNumber: addFormData.mobileNumber.trim() || "",
      facilities: facilitiesArray,
      roles: addFormData.roles
    };
    setLoading(true);
    try {
      const response = await adminServices.postData('UserManagement', payload);
      if (response.status === 200 || response.status === 201) {
        toast.success("User created successfully!");
        // Reset form
        setAddFormData({
          firstName: "",
          lastName: "",
          email: "",
          mobileNumber: "",
          password: "",
          facilities: [],
          roles: []
        });
        setSelectedFacilities([]);
        setIsAddUserModalOpen(false);
        // Refresh user list
        fetchUsers();
      }
    } catch (error) {
      console.error("Error creating user:", error);
      alert(error.response?.data?.message || "Failed to create user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Form states for Edit User
  const [editFormData, setEditFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobileNumber: "",
    password: "",
    facilities: [],
    roles: []
  });

  // Form states for Add User
  const [addFormData, setAddFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobileNumber: "",
    password: "",
    facilities: [],
    roles: []
  });

  // Form states for Add Roles
  const [roleFormData, setRoleFormData] = useState({
    roleTitle: "",
    roleCode: "",
    description: "",
    status: 1,
    // 1 for Active, 0 for Inactive
    permissionIds: []
  });

  // Form states for Generate Password
  const [passwordFormData, setPasswordFormData] = useState({
    name: "",
    email: "",
    password: "password123"
  });

  // Filter and sort data - newest users first
  const filteredData = useMemo(() => {
    const filtered = usersData.filter(user => {
      // Filter out empty placeholder rows (if any)
      if (!user.status || user.status === "") return false;

      // Apply role filter
      if (selectedRoleFilter !== "All Roles") {
        const userRoles = user.role || "";
        if (!userRoles.toLowerCase().includes(selectedRoleFilter.toLowerCase())) {
          return false;
        }
      }

      // Apply search filter if query exists
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return user.name?.toLowerCase().includes(query) || user.email?.toLowerCase().includes(query) || user.role?.toLowerCase().includes(query);
    });

    // Sort by registration date (newest first)
    return filtered.sort((a, b) => {
      // Parse dates for comparison - prefer originalData.regDate (ISO format)
      let dateA, dateB;
      if (a.originalData?.regDate) {
        dateA = new Date(a.originalData.regDate);
      } else if (a.createTime && a.createTime !== "-") {
        // Parse format: "MM/DD/YY HH:MMAM/PM"
        const timeMatch = a.createTime.match(/(\d{2})\/(\d{2})\/(\d{2})\s+(\d{1,2}):(\d{2})(AM|PM)/);
        if (timeMatch) {
          const [, month, day, year, hour, minute, ampm] = timeMatch;
          const fullYear = 2000 + parseInt(year);
          let hour24 = parseInt(hour);
          if (ampm === 'PM' && hour24 !== 12) hour24 += 12;
          if (ampm === 'AM' && hour24 === 12) hour24 = 0;
          dateA = new Date(fullYear, parseInt(month) - 1, parseInt(day), hour24, parseInt(minute));
        } else {
          dateA = new Date(0);
        }
      } else {
        dateA = new Date(0);
      }
      if (b.originalData?.regDate) {
        dateB = new Date(b.originalData.regDate);
      } else if (b.createTime && b.createTime !== "-") {
        // Parse format: "MM/DD/YY HH:MMAM/PM"
        const timeMatch = b.createTime.match(/(\d{2})\/(\d{2})\/(\d{2})\s+(\d{1,2}):(\d{2})(AM|PM)/);
        if (timeMatch) {
          const [, month, day, year, hour, minute, ampm] = timeMatch;
          const fullYear = 2000 + parseInt(year);
          let hour24 = parseInt(hour);
          if (ampm === 'PM' && hour24 !== 12) hour24 += 12;
          if (ampm === 'AM' && hour24 === 12) hour24 = 0;
          dateB = new Date(fullYear, parseInt(month) - 1, parseInt(day), hour24, parseInt(minute));
        } else {
          dateB = new Date(0);
        }
      } else {
        dateB = new Date(0);
      }

      // Descending order (newest first)
      return dateB.getTime() - dateA.getTime();
    });
  }, [usersData, searchQuery, selectedRoleFilter]);
  const columns = [{
    name: "Name",
    selector: row => row.name || "-",
    sortable: true,
    width: "15%"
  }, {
    name: "Email",
    selector: row => row.email || "-",
    sortable: true,
    width: "18%"
  }, {
    name: "Role",
    selector: row => row.role || "-",
    sortable: true,
    width: "15%"
  }, {
    name: "Create Time",
    selector: row => row.createTime || "-",
    sortable: true,
    width: "15%"
  }, {
    name: "Last login time",
    selector: row => row.lastLoginTime || "-",
    sortable: true,
    width: "15%"
  }, {
    name: "User Status",
    cell: row => {
      // Always display status toggle, even for inactive users
      if (!row.originalData && !row.id) return <div>-</div>;
      const isActive = row.status === "Active";
      const userId = row.originalData?.userId || row.id;
      return <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
      }}>
            <label className="user-access-toggle">
              <input type="checkbox" checked={isActive} onChange={e => {
            const newStatus = e.target.checked ? 1 : 0;
            if (!e.target.checked) {
              // Show confirmation modal for deactivation
              setSelectedUser({
                ...row,
                pendingStatus: newStatus
              });
              setIsDeactivateUserModalOpen(true);
            } else {
              // Activate directly
              handleStatusUpdate(userId, newStatus);
            }
          }} className="user-access-toggle-input" disabled={loading} />
              <span className="user-access-toggle-slider"></span>
            </label>
          </div>;
    },
    sortable: true,
    width: "12%",
    center: true
  }, {
    name: "Action",
    cell: row => {
      // Show actions for both active and inactive users
      if (!row.originalData && !row.id) return <div>-</div>;
      return <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "12px"
      }}>
            <button className="user-access-action-btn" onClick={() => {
          const userId = row.originalData?.userId || row.id;
          if (userId) {
            fetchUserDetails(userId);
            setIsEditUserModalOpen(true);
          }
        }} disabled={loading}>
              <Edit size={16} />
            </button>
            <button className="user-access-action-btn" onClick={() => {
          setSelectedUser(row);
          setIsDeleteUserModalOpen(true);
        }}>
              <Trash2 size={16} />
            </button>
          </div>;
    },
    sortable: false,
    width: "10%",
    center: true
  }];
  const customStyles = {
    table: {
      style: {
        tableLayout: 'fixed'
      }
    },
    headRow: {
      style: {
        backgroundColor: "#0F2A40",
        color: "#ffffff",
        fontSize: "0.875rem",
        fontWeight: 600,
        minHeight: "48px"
      }
    },
    headCells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        textTransform: "uppercase",
        textAlign: "left"
      }
    },
    rows: {
      style: {
        minHeight: "56px"
      }
    },
    cells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        paddingTop: "12px",
        paddingBottom: "12px"
      }
    },
    pagination: {
      style: {
        justifyContent: "center",
        flexWrap: "wrap"
      }
    }
  };

  // Close dropdown when clicking outside
  const filterDropdownRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = event => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setShowRoleFilterDropdown(false);
      }
    };
    if (showRoleFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showRoleFilterDropdown]);
  return <div className="dashboard-content">
      <LoadingModal show={loading} message="Loading User Access..." />
      <div className="user-access-page">
        <div className="user-access-header">
          <h1 className="dashboard-title">User Access</h1>
          <div className="user-access-header-actions">
            <button className="user-access-add-user-btn" onClick={() => {
            setAddFormData({
              firstName: "",
              lastName: "",
              email: "",
              mobileNumber: "",
              password: "",
              facilities: [],
              roles: []
            });
            setSelectedFacilities([]);
            setIsAddUserModalOpen(true);
          }}>
              <UserPlus size={18} />
              Add User
            </button>
            <button className="user-access-add-roles-btn" onClick={() => setIsAddRolesModalOpen(true)}>
              <Plus size={18} />
              Add Roles
            </button>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="user-access-search-filter">
          <div className="user-access-search-container">
            <Search className="user-access-search-icon" size={18} />
            <input type="text" placeholder="Search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="user-access-search-input" />
          </div>
          <div style={{
          position: 'relative'
        }} ref={filterDropdownRef}>
            <button className="user-access-filter-btn" onClick={() => setShowRoleFilterDropdown(!showRoleFilterDropdown)}>
              <Filter size={18} />
              Filters {selectedRoleFilter !== "All Roles" && `(${selectedRoleFilter})`}
            </button>
            {showRoleFilterDropdown && <div className="user-access-filter-dropdown" style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '8px',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            minWidth: '200px',
            zIndex: 1000,
            padding: '8px 0'
          }}>
                <div style={{
              padding: '8px 16px',
              cursor: 'pointer',
              backgroundColor: selectedRoleFilter === "All Roles" ? '#f3f4f6' : 'transparent',
              fontSize: '0.875rem'
            }} onClick={() => {
              setSelectedRoleFilter("All Roles");
              setShowRoleFilterDropdown(false);
            }} onMouseEnter={e => e.target.style.backgroundColor = '#f3f4f6'} onMouseLeave={e => e.target.style.backgroundColor = selectedRoleFilter === "All Roles" ? '#f3f4f6' : 'transparent'}>
                  All Roles
                </div>
                {roles.map(role => <div key={role.id} style={{
              padding: '8px 16px',
              cursor: 'pointer',
              backgroundColor: selectedRoleFilter === role.name ? '#f3f4f6' : 'transparent',
              fontSize: '0.875rem'
            }} onClick={() => {
              setSelectedRoleFilter(role.name);
              setShowRoleFilterDropdown(false);
            }} onMouseEnter={e => {
              if (selectedRoleFilter !== role.name) {
                e.target.style.backgroundColor = '#f3f4f6';
              }
            }} onMouseLeave={e => {
              e.target.style.backgroundColor = selectedRoleFilter === role.name ? '#f3f4f6' : 'transparent';
            }}>
                    {role.name}
                  </div>)}
              </div>}
          </div>
        </div>

        {/* Table Section */}
        <div className="user-access-table-section">
          <div className="user-access-table-container">
            <DataTable columns={columns} data={filteredData} customStyles={customStyles} striped highlightOnHover noHeader={false} pagination paginationPerPage={10} paginationRowsPerPageOptions={[]} paginationComponent={CustomPagination} paginationComponentOptions={{
            rowsPerPageText: '',
            rangeSeparatorText: 'of',
            noRowsPerPage: true,
            selectAllRowsItem: false
          }} noDataComponent={<div style={{
            padding: '40px 20px',
            textAlign: 'center',
            color: '#666',
            fontSize: '1rem'
          }}>
                  No users found
                </div>} />
          </div>
        </div>
      </div>

      {/* Edit User Modal */}
      <Modal isOpen={isEditUserModalOpen} onRequestClose={() => setIsEditUserModalOpen(false)} className="user-access-modal" overlayClassName="user-access-modal-overlay" ariaHideApp={false}>
        <div className="user-access-modal-content">
          <div className="user-access-modal-header">
            <h2 className="user-access-modal-title">Edit User</h2>
            <button className="user-access-modal-close" onClick={() => setIsEditUserModalOpen(false)}>
              <X size={20} />
            </button>
          </div>
          <div className="user-access-modal-body">
            <div className="user-access-form-field">
              <label>First Name <span style={{
                color: 'red'
              }}>*</span></label>
              <input type="text" value={editFormData.firstName || ""} onChange={e => setEditFormData({
              ...editFormData,
              firstName: e.target.value
            })} className="user-access-input" required />
            </div>
            <div className="user-access-form-field">
              <label>Last Name <span style={{
                color: 'red'
              }}>*</span></label>
              <input type="text" value={editFormData.lastName || ""} onChange={e => setEditFormData({
              ...editFormData,
              lastName: e.target.value
            })} className="user-access-input" required />
            </div>
            <div className="user-access-form-field">
              <label>Email Address <span style={{
                color: 'red'
              }}>*</span></label>
              <input type="email" value={editFormData.email || ""} onChange={e => setEditFormData({
              ...editFormData,
              email: e.target.value
            })} className="user-access-input" required />
            </div>
            <div className="user-access-form-field">
              <label>Mobile Number</label>
              <input type="tel" value={editFormData.mobileNumber || ""} onChange={e => setEditFormData({
              ...editFormData,
              mobileNumber: e.target.value
            })} className="user-access-input" placeholder="Optional" />
            </div>
            <div className="user-access-form-field">
              <label>Colony/Facilities</label>
              <div className="user-access-checkbox-group">
                {colonies.map(colony => <React.Fragment key={colony.colonyId}>
                    <label className="user-access-checkbox-label">
                      <input type="checkbox" checked={colony.facilities.some(fac => selectedEditFacilities.includes(`${colony.colonyId}-${fac.Facility || fac.facilityName}`))} onChange={e => {
                    // Toggle all facilities for this colony
                    colony.facilities.forEach(facility => {
                      const facilityName = facility.Facility || facility.facilityName;
                      const facilityKey = `${colony.colonyId}-${facilityName}`;
                      if (e.target.checked) {
                        if (!selectedEditFacilities.includes(facilityKey)) {
                          setSelectedEditFacilities(prev => [...prev, facilityKey]);
                        }
                      } else {
                        setSelectedEditFacilities(prev => prev.filter(key => key !== facilityKey));
                      }
                    });
                  }} />
                      <span>{colony.colonyName}</span>
                    </label>
                    {colony.facilities.map(facility => {
                  const facilityName = facility.Facility || facility.facilityName;
                  const facilityKey = `${colony.colonyId}-${facilityName}`;
                  return <label key={facilityKey} className="user-access-checkbox-label user-access-facilities-indent">
                          <input type="checkbox" checked={selectedEditFacilities.includes(facilityKey)} onChange={() => {
                      setSelectedEditFacilities(prev => {
                        if (prev.includes(facilityKey)) {
                          return prev.filter(key => key !== facilityKey);
                        } else {
                          return [...prev, facilityKey];
                        }
                      });
                    }} />
                          <span>{facilityName}</span>
                        </label>;
                })}
                  </React.Fragment>)}
              </div>
            </div>
            <div className="user-access-form-field">
              <label>Roles</label>
              <div className="user-access-checkbox-group roles-group">
                {roles.length > 0 ? roles.map((role, index) => {
                const col = index % 2 === 0 ? 1 : 2;
                const row = Math.floor(index / 2) + 1;
                return <label key={role.id} className="user-access-checkbox-label" style={{
                  gridColumn: col,
                  gridRow: row
                }}>
                        <input type="checkbox" checked={editFormData.roles.includes(role.id)} onChange={e => {
                    if (e.target.checked) {
                      setEditFormData({
                        ...editFormData,
                        roles: [...editFormData.roles, role.id]
                      });
                    } else {
                      setEditFormData({
                        ...editFormData,
                        roles: editFormData.roles.filter(id => id !== role.id)
                      });
                    }
                  }} />
                        <span>{role.name}</span>
                      </label>;
              }) : <div>No roles available</div>}
              </div>
            </div>
            <div className="user-access-form-field">
              <label>Password</label>
              <div className="user-access-password-field">
                <input type={showPassword ? "text" : "password"} value={editFormData.password || ""} onChange={e => setEditFormData({
                ...editFormData,
                password: e.target.value
              })} className="user-access-input" placeholder="Leave empty to keep current password" />
                <button type="button" className="user-access-password-toggle" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <button className="user-access-generate-password-btn" onClick={() => {
              // Generate a random password
              const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
              const password = Array.from({
                length: 12
              }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
              setEditFormData({
                ...editFormData,
                password
              });
            }}>
                Generate Password
              </button>
            </div>
          </div>
          <div className="user-access-modal-footer">
            <button className="user-access-cancel-btn" onClick={() => {
            setIsEditUserModalOpen(false);
            setEditFormData({
              firstName: "",
              lastName: "",
              email: "",
              mobileNumber: "",
              password: "",
              facilities: [],
              roles: []
            });
            setSelectedEditFacilities([]);
            setEditingUserId(null);
          }}>
              Cancel
            </button>
            <button className="user-access-add-btn" onClick={handleUpdateUser} disabled={loading}>
              {loading ? "Updating..." : "Update"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Add User Modal - Same as Edit User */}
      <Modal isOpen={isAddUserModalOpen} onRequestClose={() => setIsAddUserModalOpen(false)} className="user-access-modal" overlayClassName="user-access-modal-overlay" ariaHideApp={false}>
        <div className="user-access-modal-content">
          <div className="user-access-modal-header">
            <h2 className="user-access-modal-title">Add User</h2>
            <button className="user-access-modal-close" onClick={() => setIsAddUserModalOpen(false)}>
              <X size={20} />
            </button>
          </div>
          <div className="user-access-modal-body">
            <div className="user-access-form-field">
              <label>First Name <span style={{
                color: 'red'
              }}>*</span></label>
              <input type="text" value={addFormData.firstName || ""} onChange={e => setAddFormData({
              ...addFormData,
              firstName: e.target.value
            })} className="user-access-input" required />
            </div>
            <div className="user-access-form-field">
              <label>Last Name <span style={{
                color: 'red'
              }}>*</span></label>
              <input type="text" value={addFormData.lastName || ""} onChange={e => setAddFormData({
              ...addFormData,
              lastName: e.target.value
            })} className="user-access-input" required />
            </div>
            <div className="user-access-form-field">
              <label>Email Address <span style={{
                color: 'red'
              }}>*</span></label>
              <input type="email" value={addFormData.email || ""} onChange={e => setAddFormData({
              ...addFormData,
              email: e.target.value
            })} className="user-access-input" required />
            </div>
            <div className="user-access-form-field">
              <label>Mobile Number</label>
              <input type="tel" value={addFormData.mobileNumber || ""} onChange={e => setAddFormData({
              ...addFormData,
              mobileNumber: e.target.value
            })} className="user-access-input" placeholder="Optional" />
            </div>
            <div className="user-access-form-field">
              <label>Colony/Facilities</label>
              <div className="user-access-checkbox-group">
                {colonies.map(colony => <React.Fragment key={colony.colonyId}>
                    <label className="user-access-checkbox-label">
                      <input type="checkbox" checked={colony.facilities.some(fac => selectedFacilities.includes(`${colony.colonyId}-${fac.Facility || fac.facilityName}`))} onChange={e => {
                    // Toggle all facilities for this colony
                    colony.facilities.forEach(facility => {
                      const facilityName = facility.Facility || facility.facilityName;
                      const facilityKey = `${colony.colonyId}-${facilityName}`;
                      if (e.target.checked) {
                        if (!selectedFacilities.includes(facilityKey)) {
                          setSelectedFacilities(prev => [...prev, facilityKey]);
                        }
                      } else {
                        setSelectedFacilities(prev => prev.filter(key => key !== facilityKey));
                      }
                    });
                  }} />
                      <span>{colony.colonyName}</span>
                    </label>
                    {colony.facilities.map(facility => {
                  const facilityName = facility.Facility || facility.facilityName;
                  const facilityKey = `${colony.colonyId}-${facilityName}`;
                  return <label key={facilityKey} className="user-access-checkbox-label user-access-facilities-indent">
                          <input type="checkbox" checked={selectedFacilities.includes(facilityKey)} onChange={() => handleFacilityToggle(colony.colonyId, facilityName)} />
                          <span>{facilityName}</span>
                        </label>;
                })}
                  </React.Fragment>)}
              </div>
            </div>
            <div className="user-access-form-field">
              <label>Role <span style={{
                color: 'red'
              }}>*</span></label>
              <div className="user-access-checkbox-group roles-group">
                {roles.length > 0 ? roles.map((role, index) => {
                const col = index % 2 === 0 ? 1 : 2;
                const row = Math.floor(index / 2) + 1;
                const isChecked = addFormData.roles.includes(role.id);
                return <label key={role.id} className="user-access-checkbox-label" style={{
                  gridColumn: col,
                  gridRow: row
                }}>
                        <input type="checkbox" checked={isChecked} onChange={e => {
                    if (e.target.checked) {
                      // Enforce single role selection while keeping checkbox UI
                      setAddFormData({
                        ...addFormData,
                        roles: [role.id]
                      });
                    } else {
                      setAddFormData({
                        ...addFormData,
                        roles: []
                      });
                    }
                  }} />
                        <span>{role.name}</span>
                      </label>;
              }) : <div>No roles available</div>}
              </div>
            </div>
            <div className="user-access-form-field">
              <label>Password <span style={{
                color: 'red'
              }}>*</span></label>
              <div className="user-access-password-field">
                <input type={showPassword ? "text" : "password"} value={addFormData.password || ""} onChange={e => setAddFormData({
                ...addFormData,
                password: e.target.value
              })} className="user-access-input" placeholder="Enter password" required />
                <button type="button" className="user-access-password-toggle" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <button className="user-access-generate-password-btn" onClick={() => {
              // Generate a random password
              const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
              const password = Array.from({
                length: 12
              }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
              setAddFormData({
                ...addFormData,
                password
              });
            }}>
                Generate Password
              </button>
            </div>
          </div>
          <div className="user-access-modal-footer">
            <button className="user-access-cancel-btn" onClick={() => setIsAddUserModalOpen(false)}>
              Cancel
            </button>
            <button className="user-access-add-btn" onClick={handleAddUser} disabled={loading}>
              {loading ? "Adding..." : "Add"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Add Roles Modal */}
      <Modal isOpen={isAddRolesModalOpen} onRequestClose={() => {
      setIsAddRolesModalOpen(false);
      setRoleFormData({
        roleTitle: "",
        roleCode: "",
        description: "",
        status: 1,
        permissionIds: []
      });
    }} className="user-access-modal" overlayClassName="user-access-modal-overlay" ariaHideApp={false}>
        <div className="user-access-modal-content">
          <div className="user-access-modal-header">
            <h2 className="user-access-modal-title">Add Roles</h2>
            <button className="user-access-modal-close" onClick={() => setIsAddRolesModalOpen(false)}>
              <X size={20} />
            </button>
          </div>
          <div className="user-access-modal-body">
            <div className="user-access-form-field">
              <label>Role Title <span style={{
                color: 'red'
              }}>*</span></label>
              <input type="text" value={roleFormData.roleTitle || ""} onChange={e => {
              const roleTitle = e.target.value;
              // Auto-generate roleCode if empty
              const roleCode = roleFormData.roleCode || roleTitle.trim().toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');
              setRoleFormData({
                ...roleFormData,
                roleTitle: roleTitle,
                roleCode: roleFormData.roleCode || roleCode
              });
            }} className="user-access-input" required />
            </div>
            <div className="user-access-form-field">
              <label>Role Code</label>
              <input type="text" value={roleFormData.roleCode || ""} onChange={e => setRoleFormData({
              ...roleFormData,
              roleCode: e.target.value.toUpperCase().replace(/\s+/g, '_')
            })} className="user-access-input" placeholder="Auto-generated from Role Title" />
            </div>
            <div className="user-access-form-field">
              <label>Status</label>
              <select value={roleFormData.status} onChange={e => setRoleFormData({
              ...roleFormData,
              status: parseInt(e.target.value)
            })} className="user-access-input">
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
            <div className="user-access-form-field">
              <label>Description (optional)</label>
              <textarea value={roleFormData.description || ""} onChange={e => setRoleFormData({
              ...roleFormData,
              description: e.target.value
            })} className="user-access-textarea" rows={3} />
            </div>
            <div className="user-access-form-field">
              <label>Permissions</label>
              <div className="user-access-access-list" style={{
              maxHeight: '400px',
              overflowY: 'auto'
            }}>
                {permissions.length > 0 ? permissions.map(permission => <div key={permission.id} className="user-access-access-item">
                      <div style={{
                  flex: 1
                }}>
                        <span className="user-access-access-label" style={{
                    fontWeight: '500'
                  }}>
                          {permission.module || permission.key}
                        </span>
                        {permission.description && <div style={{
                    fontSize: '0.75rem',
                    color: '#6B7280',
                    marginTop: '4px'
                  }}>
                            {permission.description}
                          </div>}
                      </div>
                      <label className="user-access-toggle-label">
                        <input type="checkbox" checked={roleFormData.permissionIds.includes(permission.id)} onChange={e => {
                    if (e.target.checked) {
                      setRoleFormData({
                        ...roleFormData,
                        permissionIds: [...roleFormData.permissionIds, permission.id]
                      });
                    } else {
                      setRoleFormData({
                        ...roleFormData,
                        permissionIds: roleFormData.permissionIds.filter(id => id !== permission.id)
                      });
                    }
                  }} className="user-access-toggle-input" />
                        <span className="user-access-toggle-slider"></span>
                      </label>
                    </div>) : <div style={{
                padding: '20px',
                textAlign: 'center',
                color: '#6B7280'
              }}>
                    {loading ? 'Loading permissions...' : 'No permissions available'}
                  </div>}
              </div>
            </div>
          </div>
          <div className="user-access-modal-footer">
            <button className="user-access-cancel-btn" onClick={() => {
            setIsAddRolesModalOpen(false);
            setRoleFormData({
              roleTitle: "",
              roleCode: "",
              description: "",
              status: 1,
              permissionIds: []
            });
          }}>
              Cancel
            </button>
            <button className="user-access-add-btn" onClick={handleAddRole} disabled={loading}>
              {loading ? "Adding..." : "Add"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete User Modal */}
      <Modal isOpen={isDeleteUserModalOpen} onRequestClose={() => setIsDeleteUserModalOpen(false)} className="user-access-confirm-modal" overlayClassName="user-access-modal-overlay" ariaHideApp={false}>
        <div className="user-access-confirm-modal-content">
          <h2 className="user-access-confirm-modal-title">Delete User?</h2>
          <p className="user-access-confirm-modal-text">
            Are you sure you want to delete "{selectedUser?.name}"? This action cannot be undone.
          </p>
          <button className="user-access-delete-btn" onClick={handleDeleteUser} disabled={loading} style={{
          width: '100%',
          padding: '12px',
          backgroundColor: '#C83737',
          color: '#ffffff',
          border: 'none',
          borderRadius: '6px',
          fontSize: '0.875rem',
          fontWeight: '500',
          cursor: loading ? 'not-allowed' : 'pointer',
          fontFamily: '"Figtree", sans-serif',
          transition: 'background-color 0.2s',
          marginTop: '24px'
        }} onMouseEnter={e => {
          if (!loading) e.target.style.backgroundColor = '#B02E2E';
        }} onMouseLeave={e => {
          if (!loading) e.target.style.backgroundColor = '#C83737';
        }}>
            {loading ? "Deleting..." : "Delete"}
          </button>
        </div>
      </Modal>

      {/* Deactivate User Modal */}
      <Modal isOpen={isDeactivateUserModalOpen} onRequestClose={() => setIsDeactivateUserModalOpen(false)} className="user-access-confirm-modal" overlayClassName="user-access-modal-overlay" ariaHideApp={false}>
        <div className="user-access-confirm-modal-content">
          <h2 className="user-access-confirm-modal-title">Deactivate User</h2>
          <p className="user-access-confirm-modal-text">
            Are you sure you want to deactivate the user {selectedUser?.name}?
          </p>
          <div className="user-access-deactivate-modal-buttons">
            <button className="user-access-confirm-btn" onClick={async () => {
            if (selectedUser?.pendingStatus !== undefined && selectedUser?.originalData?.userId) {
              await handleStatusUpdate(selectedUser.originalData.userId, selectedUser.pendingStatus);
            }
            setIsDeactivateUserModalOpen(false);
            setSelectedUser(null);
          }} disabled={loading}>
              {loading ? "Updating..." : "Confirm"}
            </button>
            <button className="user-access-cancel-btn-modal" onClick={() => setIsDeactivateUserModalOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* Generate Password Modal */}
      <Modal isOpen={isGeneratePasswordModalOpen} onRequestClose={() => setIsGeneratePasswordModalOpen(false)} className="user-access-modal" overlayClassName="user-access-modal-overlay" ariaHideApp={false}>
        <div className="user-access-modal-content">
          <div className="user-access-modal-header">
            <h2 className="user-access-modal-title">Generate Password</h2>
            <button className="user-access-modal-close" onClick={() => setIsGeneratePasswordModalOpen(false)}>
              <X size={20} />
            </button>
          </div>
          <div className="user-access-modal-body">
            <div className="user-access-form-field">
              <label>Name</label>
              <input type="text" value={passwordFormData.name} onChange={e => setPasswordFormData({
              ...passwordFormData,
              name: e.target.value
            })} className="user-access-input" />
            </div>
            <div className="user-access-form-field">
              <label>Email Address</label>
              <input type="email" value={passwordFormData.email} onChange={e => setPasswordFormData({
              ...passwordFormData,
              email: e.target.value
            })} className="user-access-input" />
            </div>
            <div className="user-access-form-field">
              <label>Password</label>
              <div className="user-access-password-field">
                <input type={showPassword ? "text" : "password"} value={passwordFormData.password} onChange={e => setPasswordFormData({
                ...passwordFormData,
                password: e.target.value
              })} className="user-access-input" />
                <button type="button" className="user-access-password-toggle" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>
          <div className="user-access-modal-footer">
            <button className="user-access-cancel-btn" onClick={() => setIsGeneratePasswordModalOpen(false)}>
              Cancel
            </button>
            <button className="user-access-generate-btn">Generate</button>
          </div>
        </div>
      </Modal>
    </div>;
};
export default UserAccess;